import type { ReactNode } from "react";
import type { NavItemDividerType, NavItemType } from "../components/application/app-navigation/config";
import type { NavAccountMenuItem, NavAccountType } from "../components/application/app-navigation/base-components/nav-account-card";
import { SidebarNavigationSimple } from "../components/application/app-navigation/sidebar-navigation/sidebar-simple";

export interface DashboardShellProps {
    activeUrl: string;
    items: (NavItemType | NavItemDividerType)[];
    /** Secondary links in a quiet card above the account chip; `{ divider: true }` splits groups. */
    footerItems?: (NavItemType | NavItemDividerType)[];
    /** Small label above the footer items. */
    footerCaption?: ReactNode;
    caption?: ReactNode;
    account?: NavAccountType;
    accountMenuItems?: NavAccountMenuItem[];
    onSignOut?: () => void;
    featureCard?: ReactNode;
    /** A card under the main items, in the sidebar's free space (scrolls with the items). */
    navCard?: ReactNode;
    /** Top-bar actions (notification bell, command-menu search): desktop top bar + mobile header. */
    headerActions?: ReactNode;
    /** Desktop top bar, inline-start side (a short line of text); hidden on narrow screens. */
    headerStart?: ReactNode;
    children: ReactNode;
}

/**
 * Untitled UI "Dashboard 01" frame: fixed sidebar on the inline-start edge (right in RTL),
 * mobile header with a drawer, and a scrolling main column. `--app-top-offset` (default 0) moves
 * the fixed and sticky parts down below a page-wide bar the app puts above the shell.
 */
export const DashboardShell = ({ activeUrl, items, footerItems, footerCaption, caption, account, accountMenuItems, onSignOut, featureCard, navCard, headerActions, headerStart, children }: DashboardShellProps) => (
    <div className="flex min-h-dvh flex-col bg-primary lg:flex-row">
        <SidebarNavigationSimple
            activeUrl={activeUrl}
            items={items}
            footerItems={footerItems}
            footerCaption={footerCaption}
            caption={caption}
            account={account}
            accountMenuItems={accountMenuItems}
            onSignOut={onSignOut}
            featureCard={featureCard}
            navCard={navCard}
            mobileActions={headerActions}
        />
        <main id="main" className="relative isolate min-w-0 flex-1 bg-primary pb-16">
            {/* Soft brand wash behind the page header (blue at the inline-start, green at the inline-end). */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-96 bg-brand-soft opacity-90 [mask-image:linear-gradient(to_bottom,black_40%,transparent)] dark:opacity-70" />
            {headerActions && (
                <div className="sticky top-[var(--app-top-offset,0px)] z-30 hidden h-16 items-center justify-end gap-1 border-b border-secondary/70 bg-primary/75 px-8 backdrop-blur-md lg:flex">
                    {headerStart && <div className="me-auto flex min-w-0 flex-1 items-center pe-6">{headerStart}</div>}
                    {headerActions}
                </div>
            )}
            <div className="mx-auto flex w-full max-w-container flex-col gap-8 pt-6 lg:pt-8">{children}</div>
        </main>
    </div>
);
